import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { User as AuthUser, Session } from '@supabase/supabase-js';
import { User, AuthState, LoginCredentials, RegisterCredentials } from '../types/user';
import { supabase } from '../lib/supabase';
import { addEmailSignup } from '../lib/addEmailSignup';
import { checkAuthUserExistsByEmail } from '../lib/checkAuthUserExistsByEmail';
import { SITE_URL } from '../seo/siteMeta';

export interface ProfileUpdate {
  name?: string;
  email?: string;
}

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  logout: () => void;
  updateProfile: (updates: ProfileUpdate) => Promise<void>;
}

function mapSupabaseUserToAppUser(sbUser: AuthUser): User {
  const name =
    (sbUser.user_metadata?.full_name as string | undefined) ??
    (sbUser.user_metadata?.name as string | undefined) ??
    sbUser.email ??
    'User';
  return {
    id: sbUser.id,
    email: sbUser.email ?? '',
    name,
    avatar: sbUser.user_metadata?.avatar_url as string | undefined,
    orders: [],
    downloads: [],
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const setSession = useCallback((session: Session | null) => {
    if (!session?.user) {
      setAuthState({ user: null, isAuthenticated: false, isLoading: false });
      return;
    }
    setAuthState({
      user: mapSupabaseUserToAppUser(session.user),
      isAuthenticated: true,
      isLoading: false,
    });
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, [setSession]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setAuthState((prev) => ({ ...prev, isLoading: true }));
    const { data, error } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    });
    if (error) {
      setAuthState((prev) => ({ ...prev, isLoading: false }));
      throw new Error(error.message);
    }
    if (data.session) setSession(data.session);
  }, [setSession]);

  const register = useCallback(
    async (credentials: RegisterCredentials) => {
      setAuthState((prev) => ({ ...prev, isLoading: true }));

      // Guard: if the account already exists, do NOT call `signUp`.
      // If the guard endpoint is reachable but failing, we also avoid calling `signUp`
      // to prevent triggering extra confirmation emails.
      try {
        const { exists } = await checkAuthUserExistsByEmail(credentials.email);
        if (exists) {
          setAuthState((prev) => ({ ...prev, isLoading: false }));
          throw new Error('That email is already registered. Please log in instead.');
        }
      } catch (e) {
        // If we can reach the server but it errored, fail closed.
        setAuthState((prev) => ({ ...prev, isLoading: false }));
        const message = e instanceof Error ? e.message : 'Could not verify email status right now.';
        throw new Error(message);
      }

      const redirectTo = `${SITE_URL}/email-confirmed`;
      const { data, error } = await supabase.auth.signUp({
        email: credentials.email,
        password: credentials.password,
        options: {
          data: { full_name: credentials.name },
          emailRedirectTo: redirectTo,
        },
      });

      if (error) {
        setAuthState((prev) => ({ ...prev, isLoading: false }));
        // Handle common "email already exists" errors with a friendly message.
        const msg = error.message || '';
        if (error.code === 'email_exists' || msg.toLowerCase().includes('already registered')) {
          throw new Error('That email is already registered. Please log in instead.');
        }
        throw new Error(error.message);
      }

      // Best-effort: also capture account signups into our marketing email list.
      // (We also install a DB trigger so this stays correct even if frontend changes.)
      try {
        await addEmailSignup(credentials.email);
      } catch {
        // Ignore marketing capture failures.
      }

      if (data.session) {
        setSession(data.session);
      } else {
        setAuthState((prev) => ({ ...prev, isLoading: false }));
        throw new Error('Check your email to confirm your account.');
      }
    },
    [setSession]
  );

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setAuthState({ user: null, isAuthenticated: false, isLoading: false });
  }, []);

  const updateProfile = useCallback(
    async (updates: ProfileUpdate) => {
      const { data: { user: sbUser } } = await supabase.auth.getUser();
      if (!sbUser) return;
      const payload: { data?: Record<string, unknown>; email?: string } = {
        data: { ...sbUser.user_metadata, ...(updates.name !== undefined && { full_name: updates.name }) },
      };
      if (updates.email !== undefined) payload.email = updates.email;
      const { data, error } = await supabase.auth.updateUser(payload);
      if (error) throw new Error(error.message);
      if (data.user) {
        setAuthState((prev) => ({
          ...prev,
          user: mapSupabaseUserToAppUser(data.user),
        }));
      }
    },
    []
  );

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        register,
        logout,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 